import React, { useState, useRef } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useTenantAssets } from "../../hooks/useTenantAssets";
import {
  PlusIcon,
  PencilIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import Loader from "../common/Loader";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Input from "../common/Input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  tenantAssetCreateSchema,
  TenantAssetCreateFormData,
  TenantAsset,
  ALLOWED_IMAGE_ACCEPT,
  INVALID_IMAGE_MESSAGE,
  isStaticImageFile,
} from "../../schemas/tenantAssetSchema";
import FilePreview from "../common/FilePreview";
import { getFullMediaUrl } from "../../utils/mediaUtils";

type AssetType = "logo" | "background" | "widget" | "report_logo";

const TenantAssets: React.FC = () => {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId;

  const [selectedAssetType, setSelectedAssetType] = useState<AssetType>("logo");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<TenantAsset | null>(null);

  const {
    assets: allAssets,
    isLoading,
    createAsset,
    updateAsset,
    deleteAsset,
    isCreating,
    isUpdating,
    isDeleting,
    refetch,
  } = useTenantAssets(tenantId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setValue,
    trigger,
  } = useForm<TenantAssetCreateFormData>({
    resolver: zodResolver(tenantAssetCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      tenantId: tenantId || "",
      assetType: "logo",
    },
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenUploadModal = (assetType: AssetType) => {
    setSelectedAssetType(assetType);
    setEditingAsset(null);
    reset({
      tenantId: tenantId || "",
      assetType,
      file: undefined,
    });
    clearErrors("file");
    // Reset the file input manually to ensure it's cleared
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
    setEditingAsset(null);
    reset();
    // Reset the file input manually
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleEdit = (asset: TenantAsset) => {
    setEditingAsset(asset);
    setSelectedAssetType(asset.assetType as AssetType);
    reset({
      tenantId: tenantId || "",
      assetType: asset.assetType as AssetType,
      file: undefined,
    });
    clearErrors("file");
    // Reset the file input manually
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsUploadModalOpen(true);
  };

  const handleCreateSubmit = async (data: TenantAssetCreateFormData) => {
    if (!tenantId) {
      return;
    }

    try {
      const fileList = data.file as FileList;
      const file = fileList?.[0] as File;
      if (!file) {
        toast.error("Please select a file to upload.");
        return;
      }
      if (!isStaticImageFile(file)) {
        toast.error(INVALID_IMAGE_MESSAGE);
        return;
      }

      await createAsset({
        input: {
          assetType: selectedAssetType,
          file,
        },
      });
      handleCloseUploadModal();
      await refetch();
    } catch (error) {
      // Error is handled by toast notification
    }
  };

  const handleUpdateSubmit = async (data: TenantAssetCreateFormData) => {
    if (!editingAsset || !tenantId) {
      return;
    }

    try {
      const fileList = data.file as FileList;
      const file = fileList?.[0] as File;
      if (file && !isStaticImageFile(file)) {
        toast.error(INVALID_IMAGE_MESSAGE);
        return;
      }
      await updateAsset({
        id: editingAsset.id,
        input: {
          file: file || undefined,
        },
      });
      handleCloseUploadModal();
      await refetch();
    } catch (error) {
      // Error is handled by toast notification
    }
  };

  const handleDelete = async (asset: TenantAsset) => {
    const confirmed = window.confirm(
      `Delete ${asset.assetType.replace("_", " ")} asset? This will leave the slot blank.`
    );
    if (!confirmed) {
      return;
    }

    try {
      await deleteAsset({ id: asset.id });
      await refetch();
    } catch (error) {
      // Error is handled by toast notification
    }
  };

  const assetSections: {
    type: AssetType;
    label: string;
    description: string;
  }[] = [
    {
      type: "logo",
      label: "Logo",
      description: "Upload your tenant logo image",
    },
    {
      type: "background",
      label: "Background",
      description: "Upload your tenant background image",
    },
    {
      type: "widget",
      label: "Widget",
      description: "Upload your tenant widget file",
    },
    {
      type: "report_logo",
      label: "Report Logo",
      description: "Upload an image used at top-right of PDF/Excel reports",
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="text-center py-8 text-gray-500">
        No tenant ID found. Please contact your administrator.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-800">Tenant Assets</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {assetSections.map((section) => {
          const sectionAssets = allAssets.filter(
            (asset: TenantAsset) => asset.assetType === section.type
          );
          const currentAsset = sectionAssets[0] || null;

          return (
            <div
              key={section.type}
              className="bg-white border border-gray-200 rounded-lg shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {section.label}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {section.description}
                  </p>
                </div>
              </div>

              {currentAsset ? (
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    {currentAsset.thumbnailUrl ||
                    currentAsset.fileUrl ? (
                      <div className="mb-3">
                        <FilePreview
                          name={currentAsset.fileName}
                          type={currentAsset.format}
                          url={getFullMediaUrl(
                            currentAsset.thumbnailUrl ||
                            currentAsset.fileUrl
                          )}
                          className="w-full h-48 object-contain rounded-lg"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32 bg-gray-100 rounded">
                        <PhotoIcon className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {currentAsset.fileName}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Format: {currentAsset.format}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(currentAsset)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-default text-white rounded-md hover:bg-primary-dark transition-colors"
                      disabled={isUpdating || isCreating || isDeleting}
                    >
                      <PencilIcon className="h-4 w-4" />
                      Update
                    </button>
                    <button
                      onClick={() => handleDelete(currentAsset)}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                      disabled={isUpdating || isCreating || isDeleting}
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 mb-4">
                    No {section.label.toLowerCase()} uploaded
                  </p>
                  <button
                    onClick={() => handleOpenUploadModal(section.type)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-default text-white rounded-md hover:bg-primary-dark transition-colors mx-auto"
                    disabled={isUpdating || isCreating || isDeleting}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Upload {section.label}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Upload/Update Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        title={
          editingAsset
            ? `Update ${selectedAssetType}`
            : `Upload ${selectedAssetType}`
        }
        size="md"
      >
        <form
          onSubmit={handleSubmit(
            editingAsset ? handleUpdateSubmit : handleCreateSubmit
          )}
          className="space-y-4"
        >
          <div>
            <Input
              label={editingAsset ? "New File (Optional)" : "File *"}
              name="file"
              type="file"
              accept={ALLOWED_IMAGE_ACCEPT}
              onChange={(e) => {
                // Manually set the FileList to ensure React Hook Form captures it correctly
                if (e.target.files && e.target.files.length > 0) {
                  // Reject anything that isn't a static image (no GIF, no video).
                  // The `accept` filter is advisory only — users can still pick
                  // other files via "All files", so enforce it here too.
                  if (!isStaticImageFile(e.target.files[0])) {
                    toast.error(INVALID_IMAGE_MESSAGE);
                    e.target.value = "";
                    setValue("file", undefined as any, { shouldValidate: false });
                    return;
                  }
                  setValue("file", e.target.files as any, { shouldValidate: false });
                  // Clear error immediately when file is selected
                  clearErrors("file");
                } else {
                  // Clear the value if no file is selected
                  setValue("file", undefined as any, { shouldValidate: false });
                }
              }}
              onBlur={() => {
                // Don't trigger file validation on blur — clicking the file input
                // opens a native dialog which fires blur immediately before any
                // file is selected, causing a premature "File is required" error.
              }}
              error={editingAsset ? undefined : (errors.file as any)}
              ref={fileInputRef}
            />
            {/* {selectedFile && selectedFile.length > 0 && (
              <p className="mt-2 text-sm text-gray-600">
                Selected: {selectedFile[0].name}
              </p>
            )} */}
            {editingAsset && (
              <p className="mt-2 text-sm text-gray-500">
                Current file: {editingAsset.fileName}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCloseUploadModal}
              disabled={isUpdating || isCreating || isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={editingAsset ? isUpdating : isCreating}
              disabled={isDeleting}
            >
              {editingAsset ? "Update" : "Upload"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default TenantAssets;
